// src/pages/Home/Home.js
import React from 'react';
import { Link } from 'react-router-dom';
import './Home.css';
import EarlyAccessForm from '../../components/EarlyAccessForm/EarlyAccessForm';

const Home = () => {
  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="video-header">
        <div className="video-overlay"></div>
        <video
          className="video-background"
          autoPlay
          muted
          loop
          playsInline
          disablePictureInPicture
        >
          <source src="PSHome.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="header-content">
          <h1 className="header-wordmark">
            <span className="wordmark-pointe">Pointe</span><span className="wordmark-sense-white">Sense</span>
          </h1>
          <p className="header-subtext">Smart insight for pointe work, so dancers train smarter, stay safer, and dance longer</p>
          <div className="header-cta">
            <Link to="/beta-testing" className="beta-cta-button">
              Join Our Beta Testing
            </Link>
          </div>
        </div>
      </section>

      {/* Video and Photo Section */}
      <section className="media-section">
        <div className="media-container">
          {/* Horizontal Video */}
          <div className="video-horizontal">
            <div className="home-hero-text">
              <h1 className="home-hero-title">
                What if your pointe shoes could talk back?
              </h1>
              <p className="home-hero-subtitle">
                PointeSense turns smart pressure sensing into real-time shoe fit and technique feedback, so dancers train smarter & stay healthier, longer.
                <br />
                <em>measure. improve. move.</em>
              </p>
            </div>
          </div>

          {/* Vertical Video */}
          <div className="video-vertical">
            <div className="video-wrapper">
              <video
                autoPlay
                muted
                loop
                playsInline
                disablePictureInPicture
                className="featured-video"
              >
                <source src="PointeSense_Vertical.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Why Section */}
      <section className="why-section">
        <div className="content-wrapper">
          <h2 className="section-heading">Why we built PointeSense</h2>
          <h3 className="why-subheading">Good fitting prevents injuries, and it&rsquo;s skilled work.</h3>
          <p className="why-text">
            A pointe fitter reads feet, shoes and movement through years of experience, and a good
            fit is one of the best ways to prevent injury. But what fitters can see is limited to
            the outside of the shoe: what the dancer reports, how the foot looks, how the shoe holds
            up over time. Even a great fitting is hard to explain, and hard to show a worried parent
            or a teacher.
          </p>
          <p className="why-text">
            We&rsquo;re not here to replace the fitter&rsquo;s eye. We&rsquo;re here to back it up.
            PointeSense gives fitters a quantitative view of what&rsquo;s happening inside the shoe,
            so what you&rsquo;ve noticed can be seen, measured and explained.
          </p>
          <h3 className="why-subheading">What if&hellip;</h3>
          <ul className="why-whatif">
            <li>
              <strong>&hellip;you could see what led to a problem?</strong> When a dancer is
              struggling with a shoe, or has been injured, you could look at how pressure moved and
              where it concentrated, and explain what your experience was already telling you.
            </li>
            <li>
              <strong>&hellip;dancers could learn what a supportive shoe looks like?</strong> Not
              just &ldquo;it feels right&rdquo;, but what good support looks like in the data, in
              terms they can understand.
            </li>
            <li>
              <strong>&hellip;your experience and the numbers worked together?</strong> Combine what
              you&rsquo;ve learned over years of fittings with objective feedback, for work that is
              comfortable and safe.
            </li>
            <li>
              <strong>&hellip;we closed the communication gap between dancers and fitters?</strong>{' '}
              A dancer who is just starting pointe doesn&rsquo;t yet know what feels right. A shared
              picture of the fit gives you both a common language.
            </li>
            <li>
              <strong>&hellip;you could show dancers, parents and teachers that a fit is good?</strong>{' '}
              A clear, visual record that this shoe is a supportive fit for this dancer.
            </li>
          </ul>
          <p className="why-text why-closing">
            PointeSense is a tool for fitters, built to help you fit well and to show that you did.
          </p>
          <h3 className="why-subheading">Why fit is worth getting right</h3>
          <p className="why-text">
            Ballet dancers experience foot and ankle injury rates of 67&ndash;95%. Professional
            companies report more than 1.3 injuries per dancer, per season, driven primarily
            by the extreme biomechanical demands of pointe work.
            <sup>
              <a href="#ref-1">[1]</a>
              <a href="#ref-2">[2]</a>
            </sup>
            {' '}Biomechanical studies point to three critical risk factors: shoe condition, overuse
            injury, and foot biomechanics.
            <sup>
              <a href="#ref-3">[3]</a>
              <a href="#ref-4">[4]</a>
            </sup>
            {' '}A well-fitted shoe is one of the few of those factors we can act on before an
            injury happens.
          </p>
          <p className="why-text why-story-link">
            <Link to="/about">Read our story &rarr;</Link>
          </p>
        </div>
      </section>

      {/* What PointeSense Is */}
      <section className="product-explainer-section">
        <div className="content-wrapper product-explainer-wrapper">
          <div className="explainer-grid">
            <div className="explainer-text-col">
              <h2 className="section-heading">
                What is <span className="wordmark-pointe">Pointe</span><span className="wordmark-sense-navy">Sense</span>?
              </h2>
              <p className="explainer-text">
                PointeSense is a smart wearable insert that shows you exactly what&rsquo;s happening
                between your foot and your pointe shoe. Our patent-pending technology includes a 3D
                pressure insert, worn inside the shoe, that covers the box, platform, and partially up
                the shank, capturing pressure readings at 80+ points. The insert connects to a
                &ldquo;Core&rdquo; worn around the dancer&rsquo;s arch, with a built-in IMU that adds
                important movement context.
              </p>

              <h3 className="visualize-heading">That means we can visualize things like:</h3>
              <ul className="visualize-list">
                <li>Where your weight is being supported</li>
                <li>Any twisting or imbalance of loads transferred from foot to shoe</li>
                <li>Painful hotspots, and how severe they are</li>
                <li>How well you&rsquo;re rolling through your shoes and absorbing impact during jumps</li>
                <li>Turn consistency and turn axis</li>
                <li>What impulses your toes experience while on pointe</li>
              </ul>
              <p className="visualize-more">And so much more&hellip;</p>
            </div>

            <div className="explainer-image-col">
              <img
                src={`${process.env.PUBLIC_URL}/images/PS_1foot.png`}
                alt="PointeSense pressure visualization on a dancer's foot"
                className="explainer-image"
              />
              <div className="explainer-cta">
                <Link to="/how-it-works" className="beta-cta-button">
                  See How It Works
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fit Section */}
      <section className="fit-section">
        <div className="content-wrapper">
          <h2 className="section-heading">But it all starts with pointe shoe fit.</h2>
          <p className="fit-text">
            Ever had a pair of pointe shoes you never wore again, or that caused pain, bruised
            toenails, or worse, injury? Pointe shoe fitting is a difficult process with so many
            variables: length, width, vamp, stiffness, shape, and much more.
          </p>
          <p className="fit-statement">Pointe work should not hurt.</p>
          <p className="fit-text">
            New dancers are just learning what pointe shoes are supposed to feel like, and can
            struggle to explain to their fitter what they&rsquo;re feeling. Many dancers and fitters
            end up settling for a fit that&rsquo;s just a bit off. Our technology is being designed to
            support dancers and fitters in finding the best solution together.
          </p>

          <div className="fit-callout">
            <p>We are not replacing fitters.</p>
            <p>We are not AI telling you what shoe to wear.</p>
            <p className="fit-callout-emphasis">PointeSense is providing insights.</p>
          </div>
        </div>
      </section>

      {/* Use Cases / Life Cycle Section */}
      <section className="journey-section">
        <div className="content-wrapper journey-content-wrapper">
          <h2 className="section-heading">A Journey Through Pointe</h2>
          <p className="journey-intro">
            PointeSense is designed to support dancers at every stage, from their very first fitting
            to professional performance and injury recovery.
          </p>
          <div className="journey-grid">
            <div className="journey-step">
              <div className="journey-number">1</div>
              <h3 className="journey-title">First Fitting</h3>
              <p className="journey-text">
                A young dancer gets her first pointe shoe fitting. Her fitter uses PointeSense to see
                pressure distribution and hotspots inside the shoe in real time, helping find a fit
                that&rsquo;s comfortable and supportive from day one.
              </p>
            </div>
            <div className="journey-step">
              <div className="journey-number">2</div>
              <h3 className="journey-title">Training in the Studio</h3>
              <p className="journey-text">
                In class, her teacher uses PointeSense to spot instability before it becomes an
                injury, catching a fatigued, wobbly roll-through early enough to correct it rather
                than lose weeks to recovery.
              </p>
            </div>
            <div className="journey-step">
              <div className="journey-number">3</div>
              <h3 className="journey-title">Practice at Home</h3>
              <p className="journey-text">
                As she improves, she practices at home with her own PointeSense, reviewing feedback
                on her roll-through, turns, and jump landings to reinforce good habits between
                classes.
              </p>
            </div>
            <div className="journey-step">
              <div className="journey-number">4</div>
              <h3 className="journey-title">Professional Career</h3>
              <p className="journey-text">
                Years later, recovering from an injury as a professional, her physiotherapist uses
                PointeSense to track twisting and weakness inside the shoe until she&rsquo;s cleared
                to perform again.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Beta / Collaboration Section */}
      <section className="beta-section">
        <div className="content-wrapper">
          <h2 className="section-heading">Join us in beta testing</h2>
          <p className="beta-text">
            Our first step toward injury prevention is addressing pointe shoe fit. We&rsquo;re looking
            for dancers, studios, dance retail stores, pointe shoe companies, professional companies,
            and any other potential collaborators open to beta testing and helping us collect
            information on pointe shoe fit. With permission, we&rsquo;ll collect data on dancers&rsquo; foot
            anatomy and pointe shoe models, and, with the support of experienced fitters, train our
            models to identify potential problems with pointe shoe fit.
          </p>
          <p className="beta-text">
            In the future, we&rsquo;ll expand further into training as well. We&rsquo;re also open to
            research institutions and other organizations hoping to quantify technique, pointe shoe
            fit, biomechanics, or injury risk from pointe work.
          </p>
          <div className="beta-cta">
            <Link to="/beta-testing" className="beta-cta-button">
              See the Call for Beta Testers
            </Link>
          </div>
        </div>
      </section>

      {/* Early Access Section */}
      <section className="early-access-section" id="early-access">
        <div className="content-wrapper early-access-content">
          <h2 className="section-heading light">
            Be among the first to use <span className="wordmark-pointe">Pointe</span><span className="wordmark-sense-white">Sense</span>
          </h2>
          <p className="early-access-subtext">
            Join the early access list to receive product updates, research notes, and invitations
            to join beta testing or purchase when units become available.
          </p>

          <div className="early-access-card">
            <EarlyAccessForm idPrefix="home" />
          </div>
        </div>
      </section>

      <section className="image-section">
        <div className="image-section">
          <div className="image-container">
            <img src={`${process.env.PUBLIC_URL}/PS_nobackground_crossed.png`} alt="PointeSense on a dancer" className="side-image" />
          </div>
        </div>
      </section>

      {/* Partners & Support Section */}
      <section className="partners-support-section">
        <h2 className="section-title">Our Partners & Support</h2>
        <div className="partners-container">
          <div className="partner">
            <img src="logos/JHU.logo_horizontal.white.png" alt="Johns Hopkins University" className="partner-logo" />
          </div>
          <div className="separator"></div>
          <div className="partner">
            <img src="logos/PAVA.png" alt="PAVA Marie LaPere Center for Entrepreneurship" className="partner-logo" />
          </div>
          <div className="partner">
            <img src="logos/TEDCO.png" alt="TEDCO" className="partner-logo" />
          </div>
          <div className="separator"></div>
          <div className="partner">
            <img src="logos/MII.png" alt="Maryland Innovation Initiative" className="partner-logo" />
          </div>
        </div>
      </section>

      {/* References */}
      <section className="references-section">
        <div className="content-wrapper">
          <h3 className="references-heading">References</h3>
          <ol className="references-list">
            <li id="ref-1">
              Katakura, M., Kedgley, A. E., Shaw, J. W., Mattiussi, A. M., Kelly, S., Clark, R.,
              Allen, N., &amp; Calder, J. D. F. (2023). Epidemiological Characteristics of Foot and
              Ankle Injuries in 2 Professional Ballet Companies: A 3-Season Cohort Study of 588
              Medical Attention Injuries and 255 Time-Loss Injuries. <em>Orthopaedic Journal of
              Sports Medicine</em>.{' '}
              <a href="https://doi.org/10.1177/23259671221134131" target="_blank" rel="noopener noreferrer">
                doi:10.1177/23259671221134131
              </a>
            </li>
            <li id="ref-2">
              Shaw, J. W., Mattiussi, A. M., Clark, R., &amp; Kelly, S. (2024). Epidemiology and
              management of ankle sprain injuries over seven seasons in an elite professional ballet
              company. <em>Journal of Science and Medicine in Sport</em>.
            </li>
            <li id="ref-3">
              Li, F., Adrien, N., &amp; He, Y. (2022). Biomechanical Risks Associated with Foot and
              Ankle Injuries in Ballet Dancers: A Systematic Review. <em>International Journal of
              Environmental Research and Public Health</em>.{' '}
              <a href="https://doi.org/10.3390/ijerph19084916" target="_blank" rel="noopener noreferrer">
                doi:10.3390/ijerph19084916
              </a>
            </li>
            <li id="ref-4">
              Madden, K., Mayes, S., Cook, J., &amp; Ferrar, K. (2024). The Effects of Pointe Shoes
              on Ballet Dancers&rsquo; Biomechanics, Muscle Activity, Movement and Symptoms: A Scoping
              Review. <em>Journal of Dance Medicine &amp; Science</em>.{' '}
              <a href="https://doi.org/10.1177/1089313X231218305" target="_blank" rel="noopener noreferrer">
                doi:10.1177/1089313X231218305
              </a>
            </li>
          </ol>
        </div>
      </section>
    </div>
  );
};

export default Home;
